import React, { useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Button } from 'components/Button';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import QRCode from 'react-qr-code';

import {
  StyledQRcodeList,
  PDFContainer,
  WhiteSheet,
  A4Sheet,
  A4SheetLandscape,
  Grid91,
  Grid18_3x6,
  Grid10_2x5,
  Single100x50,
  Single60x40,

  // Layout TWO (2,55 × 2,09 cm – 91 por A4)
  EtiquetaTwo,
  ItemTwo,
  MainTwo,
  NameBoxTwo,

  // Layout 7.59x3.20 (7,59 × 3,20 cm – 18 por A4)
  Etiqueta759x320,
  Item759x320,
  Main759x320,
  TopRow759x320,
  CompanyLogo759x320,
  QRCode759x320,
  Brand759x320,
  BottomRow759x320,
  NameText759x320,
  TagText759x320,

  // Layout 10x5-pimaco (10×5 cm – 10 por A4)
  EtiquetaPimaco,
  ItemPimaco,
  MainPimaco,
  TopRowPimaco,
  CompanyLogoPimaco,
  QRCodePimaco,
  BrandPimaco,
  BottomRowPimaco,
  NameTextPimaco,
  TagTextPimaco,

  // Layout 10x5-single (10×5 cm – 1 por rolo)
  EtiquetaRoll,
  ItemRoll,
  MainRoll,
  TopRowRoll,
  CompanyLogoRoll,
  QRCodeRoll,
  BrandRoll,
  BottomRowRoll,
  NameTextRoll,
  TagTextRoll,

  // Layout 6x4-single (6×4 cm – 1 por rolo)
  EtiquetaRoll64,
  ItemRoll64,
  MainRoll64,
  TopRowRoll64,
  CompanyLogoRoll64,
  QRCodeRoll64,
  BrandRoll64,
  BottomRowRoll64,
  NameTextRoll64,
  TagTextRoll64,
} from './styled';

import LeankeepVerticalLogo from 'assets/images/logo-leankeep-vertical.png';

// etiquetas por página por formato
const PER_PAGE = {
  '2.55x2.09': 91, // 7 x 13
  '7.59x3.20': 18, // 3 x 6
  '10x5-pimaco': 10, // 2 x 5
};

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/* ===== Nome auto-escalável para o Layout Two (91/A4) ===== */
const NameFit = ({ children, pt = 8 }) => {
  const boxRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const span = textRef.current;
    if (!box || !span) return;

    const measure = () => {
      span.style.transform = 'scale(1)';
      const bw = box.clientWidth || 1;
      const bh = box.clientHeight || 1;
      const sw = span.scrollWidth || 1;
      const sh = span.scrollHeight || 1;
      const scale = Math.min(1, bw / sw, bh / sh);
      span.style.transform = `scale(${scale})`;
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [children]);

  return (
    <NameBoxTwo ref={boxRef} title={String(children || '')}>
      <span
        ref={textRef}
        style={{
          display: 'inline-block',
          transformOrigin: 'left center',
          whiteSpace: 'nowrap',
          fontFamily: 'Roboto',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: `${pt}px`,
          lineHeight: '100%',
          letterSpacing: '0%',
        }}
      >
        {children}
      </span>
    </NameBoxTwo>
  );
};

/* ===== Layout 2 (2,55 × 2,09 cm – 91/A4) ===== */
const QRCodeLayoutTwo = ({ item, tipoEtiqueta, buildQRCodeLink, line1Text = '', fontPt = 8 }) => (
  <EtiquetaTwo>
    <ItemTwo>
      <MainTwo>
        <div style={{ width: '70%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <QRCode style={{ width: '40px', height: '41px' }} value={buildQRCodeLink(item, tipoEtiqueta)} />
        </div>
        <div style={{ width: '30%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={LeankeepVerticalLogo}
            alt="Leankeep"
            style={{ width: '90%', height: '90%', objectFit: 'contain' }}
          />
        </div>
      </MainTwo>
      <NameFit pt={fontPt}>{line1Text}</NameFit>
    </ItemTwo>
  </EtiquetaTwo>
);

/* ===== Layout 7.59x3.20 (7,59 × 3,20 cm – 18/A4) ===== */
const QRCodeLayout759x320 = ({
  item,
  tipoEtiqueta,
  logotipo,
  buildQRCodeLink,
  buildImageSrc,
  line1Text = '',
  line2Text = '',
  fontPt = 9,
}) => (
  <Etiqueta759x320>
    <Item759x320>
      <Main759x320>
        {/* Linha de cima: LOGO | QR | MARCA vertical */}
        <TopRow759x320>
          <CompanyLogo759x320>
            <img
              src={buildImageSrc(item, tipoEtiqueta, logotipo)}
              alt="Logo"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </CompanyLogo759x320>

          <QRCode759x320>
            <QRCode style={{ width: '77px', height: '82px' }} value={buildQRCodeLink(item, tipoEtiqueta)} />
          </QRCode759x320>

          <Brand759x320>
            <img src={LeankeepVerticalLogo} alt="Leankeep" />
          </Brand759x320>
        </TopRow759x320>

        {/* Linha de baixo: NOME (acima) e TAG (abaixo) */}
        <BottomRow759x320>
          <NameText759x320 $sizePt={fontPt} title={line1Text}>
            {line1Text}
          </NameText759x320>
          <TagText759x320 $sizePt={fontPt} title={line2Text}>
            {line2Text}
          </TagText759x320>
        </BottomRow759x320>
      </Main759x320>
    </Item759x320>
  </Etiqueta759x320>
);

/* ===== Layout 10x5-pimaco (10×5 cm – 10 por A4) ===== */
const QRCodeLayoutPimaco = ({
  item,
  tipoEtiqueta,
  logotipo,
  buildQRCodeLink,
  buildImageSrc,
  line1Text = '',
  line2Text = '',
  fontPt = 9,
}) => (
  <EtiquetaPimaco>
    <ItemPimaco>
      <MainPimaco>
        {/* Linha de cima: LOGO | QR | MARCA vertical */}
        <TopRowPimaco>
          <CompanyLogoPimaco>
            <img
              src={buildImageSrc(item, tipoEtiqueta, logotipo)}
              alt="Logo"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </CompanyLogoPimaco>

          <QRCodePimaco>
            <QRCode style={{ width: '110px', height: '110px' }} value={buildQRCodeLink(item, tipoEtiqueta)} />
          </QRCodePimaco>

          <BrandPimaco src={LeankeepVerticalLogo} alt="Leankeep" />
        </TopRowPimaco>

        {/* Linha de baixo: NOME (acima) e TAG (abaixo) */}
        <BottomRowPimaco>
          <NameTextPimaco $sizePt={fontPt} title={line1Text}>
            {line1Text}
          </NameTextPimaco>
          <TagTextPimaco $sizePt={fontPt} title={line2Text}>
            {line2Text}
          </TagTextPimaco>
        </BottomRowPimaco>
      </MainPimaco>
    </ItemPimaco>
  </EtiquetaPimaco>
);

/* ===== Layout 10x5-single (10×5 cm – 1 por rolo) ===== */
const QRCodeLayoutRoll = ({
  item,
  tipoEtiqueta,
  logotipo,
  buildQRCodeLink,
  buildImageSrc,
  line1Text = '',
  line2Text = '',
  fontPt = 8,
}) => (
  <EtiquetaRoll>
    <ItemRoll>
      <MainRoll>
        {/* Linha de cima: LOGO | QR | MARCA */}
        <TopRowRoll>
          <CompanyLogoRoll>
            <img
              src={buildImageSrc(item, tipoEtiqueta, logotipo)}
              alt="Logo"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </CompanyLogoRoll>

          <QRCodeRoll>
            <QRCode style={{ width: '30mm', height: '30mm' }} value={buildQRCodeLink(item, tipoEtiqueta)} />
          </QRCodeRoll>

          <BrandRoll src={LeankeepVerticalLogo} alt="Leankeep" />
        </TopRowRoll>

        {/* Linha de baixo: NOME (acima) e TAG (abaixo) */}
        <BottomRowRoll>
          <NameTextRoll $sizePt={fontPt} title={line1Text}>
            {line1Text}
          </NameTextRoll>
          <TagTextRoll $sizePt={fontPt} title={line2Text}>
            {line2Text}
          </TagTextRoll>
        </BottomRowRoll>
      </MainRoll>
    </ItemRoll>
  </EtiquetaRoll>
);

/* ===== Layout 6x4-single (6×4 cm – 1 por rolo) ===== */
const QRCodeLayoutRoll64 = ({
  item,
  tipoEtiqueta,
  logotipo,
  buildQRCodeLink,
  buildImageSrc,
  line1Text = '',
  line2Text = '',
  fontPt = 7,
}) => (
  <EtiquetaRoll64>
    <ItemRoll64>
      <MainRoll64>
        {/* Linha de cima: LOGO | QR | MARCA */}
        <TopRowRoll64>
          <CompanyLogoRoll64>
            <img
              src={buildImageSrc(item, tipoEtiqueta, logotipo)}
              alt="Logo"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </CompanyLogoRoll64>

          <QRCodeRoll64>
            <QRCode style={{ width: '20mm', height: '20mm' }} value={buildQRCodeLink(item, tipoEtiqueta)} />
          </QRCodeRoll64>

          <BrandRoll64 src={LeankeepVerticalLogo} alt="Leankeep" />
        </TopRowRoll64>

        {/* Linha de baixo: NOME (acima) e TAG (abaixo) */}
        <BottomRowRoll64>
          <NameTextRoll64 $sizePt={fontPt} title={line1Text}>
            {line1Text}
          </NameTextRoll64>
          <TagTextRoll64 $sizePt={fontPt} title={line2Text}>
            {line2Text}
          </TagTextRoll64>
        </BottomRowRoll64>
      </MainRoll64>
    </ItemRoll64>
  </EtiquetaRoll64>
);

export const QRcodesList = () => {
  const location = useLocation();

  // Lê os dados do sessionStorage ou fallback para location.state
  const data = useMemo(() => {
    const storedData = sessionStorage.getItem('qrcodeData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      return parsed.data || [];
    }
    return location.state?.data || [];
  }, [location.state?.data]);

  const configuracao = useMemo(() => {
    const storedData = sessionStorage.getItem('qrcodeData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      return parsed.configuracoesImpressao || {};
    }
    return location.state?.configuracoesImpressao || {};
  }, [location.state?.configuracoesImpressao]);

  const logotipo = configuracao?.logotipo;
  const tipoEtiqueta = configuracao?.tipoEtiqueta; // 'typeArea' | 'typeUnit' | 'typeEquip'
  const formatoEtiqueta = configuracao?.formatoEtiqueta; // '2.55x2.09' | '7.59x3.20' | '10x5-pimaco' | '10x5-single' | '6x4-single'
  const fontPt = Number(configuracao?.fontSize) || 9;

  // descrições escolhidas no formulário. Se nada vier, padrão deve ser:
  // linha 1 => item.nome ; linha 2 => item.tag
  const desc1Key = configuracao?.description1 || 'defaultName';
  const desc2Key = configuracao?.description2 || 'defaultTag';

  // injeta o @page correto para o formato ativo
  useEffect(() => {
    const styleTag = document.createElement('style');
    let pageCss = `@media print { @page { size: A4; margin: 0; } }`;
    if (formatoEtiqueta === '7.59x3.20') pageCss = `@media print { @page { size: A4 landscape; margin: 0; } }`;
    if (formatoEtiqueta === '10x5-single') pageCss = `@media print { @page { size: 100mm 50mm; margin: 0; } }`;
    if (formatoEtiqueta === '6x4-single') pageCss = `@media print { @page { size: 60mm 40mm; margin: 0; } }`;
    styleTag.innerHTML = pageCss;
    document.head.appendChild(styleTag);
    return () => document.head.removeChild(styleTag);
  }, [formatoEtiqueta]);

  const buildQRCodeLink = (item, tipo) => {
    const baseUrl = 'https://qr.lkp.app.br/';
    if (tipo === 'typeArea') return `${baseUrl}A_${item.area}`;
    if (tipo === 'typeUnit') return `${baseUrl}S_${item.site}`;
    return `${baseUrl}E_${item.equipamento}`;
  };

  const buildImageSrc = (item, tipo, logoSel) => {
    const baseUrl = process.env.REACT_APP_URL_IMAGES;
    // const baseUrl =
    //   'https://static.lkp.app.br/cdn-cgi/image/fit=pad,w=400,h=200,background=%23FFFFFF,quality=95/https:/media.lkp.app.br/v1/static/';
    if (tipo === 'typeUnit') return baseUrl + item.logomarcaPath;
    if (logoSel === 'companyLogo') {
      return baseUrl + item?.siteObj?.empresaObj?.logomarcaPath;
    }
    if (logoSel === 'unitLogo') return baseUrl + item?.siteObj?.logomarcaPath;
    return baseUrl + (item?.logomarcaPath || '');
  };

  // resolve a string conforme a seleção do usuário
  const resolveText = (item, tipo, key) => {
    const safe = v => (v == null ? '' : String(v));

    // defaults exigidos
    if (key === 'defaultName') return safe(item?.nome);
    if (key === 'defaultTag') return safe(item?.tag);

    // nenhum
    if (!key || key === 'none') return '';

    if (tipo === 'typeArea') {
      switch (key) {
        case 'areaName':
          return safe(item?.nome || item?.areaObj?.nome);
        case 'environmentId':
          return safe(item?.identificacaoAmbiente || item?.areaObj?.identificacaoAmbiente);
        case 'unitName':
          return safe(item?.siteObj?.nome);
        case 'areaGroup':
          return safe(item?.grupoAreaObj?.agrupamento || item?.areaObj?.grupoAreaObj?.agrupamento);
        default:
          return '';
      }
    }

    // typeEquip / typeUnit (equipamento)
    switch (key) {
      case 'equipmentName':
        return safe(item?.nome);
      case 'equipmentTag':
        return safe(item?.tag);
      case 'unitName':
        return safe(item?.siteObj?.nome);
      case 'areaGroup':
        return safe(item?.areaObj?.grupoAreaObj?.agrupamento);
      case 'areaName':
        return safe(item?.areaObj?.nome);
      default:
        return '';
    }
  };

  const perPage = PER_PAGE[formatoEtiqueta];
  const pages = useMemo(() => (perPage ? chunk(data, perPage) : [data]), [data, perPage]);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  return (
    <StyledQRcodeList>
      <PDFContainer>
        <WhiteSheet>
          <div ref={printRef}>
            {/* ===== 91 por página (2,55 × 2,09 cm) ===== */}
            {formatoEtiqueta === '2.55x2.09' &&
              pages.map((items, p) => (
                <A4Sheet key={`p91-${p}`} padding="12.65mm 15.75mm">
                  <Grid91>
                    {items.map((item, i) => (
                      <QRCodeLayoutTwo
                        key={`l91-${p}-${i}`}
                        item={item}
                        tipoEtiqueta={tipoEtiqueta}
                        buildQRCodeLink={buildQRCodeLink}
                        line1Text={resolveText(item, tipoEtiqueta, desc1Key)}
                        fontPt={fontPt}
                      />
                    ))}
                  </Grid91>
                </A4Sheet>
              ))}

            {/* ===== 18 por página (7,4 × 3,10 cm) - Layout Horizontal Colado ===== */}
            {formatoEtiqueta === '7.59x3.20' &&
              pages.map((items, p) => (
                <A4SheetLandscape key={`p18-${p}`} padding="10mm 20mm">
                  <Grid18_3x6>
                    {items.map((item, i) => (
                      <QRCodeLayout759x320
                        key={`l18-${p}-${i}`}
                        item={item}
                        tipoEtiqueta={tipoEtiqueta}
                        logotipo={logotipo}
                        buildQRCodeLink={buildQRCodeLink}
                        buildImageSrc={(it, t) => buildImageSrc(it, t, logotipo)}
                        line1Text={resolveText(item, tipoEtiqueta, desc1Key)}
                        line2Text={resolveText(item, tipoEtiqueta, desc2Key)}
                        fontPt={fontPt}
                      />
                    ))}
                  </Grid18_3x6>
                </A4SheetLandscape>
              ))}

            {/* ===== Pimaco A4250 (10 por A4, 100×50 mm) ===== */}
            {formatoEtiqueta === '10x5-pimaco' &&
              pages.map((items, p) => (
                <A4Sheet key={`p10-${p}`} padding="10mm 10mm">
                  <Grid10_2x5>
                    {items.map((item, i) => (
                      <div
                        key={`l10-${p}-${i}`}
                        style={{
                          gridColumn: i % 2 === 0 ? 1 : 3,
                          gridRow: Math.floor(i / 2) * 2 + 1,
                        }}
                      >
                        <QRCodeLayoutPimaco
                          item={item}
                          tipoEtiqueta={tipoEtiqueta}
                          logotipo={logotipo}
                          buildQRCodeLink={buildQRCodeLink}
                          buildImageSrc={(it, t) => buildImageSrc(it, t, logotipo)}
                          line1Text={resolveText(item, tipoEtiqueta, desc1Key)}
                          line2Text={resolveText(item, tipoEtiqueta, desc2Key)}
                          fontPt={fontPt}
                        />
                      </div>
                    ))}
                  </Grid10_2x5>
                </A4Sheet>
              ))}

            {/* ===== 10×5 cm (1 por rolo) ===== */}
            {formatoEtiqueta === '10x5-single' &&
              data.map((item, i) => (
                <Single100x50 key={`s105-${i}`}>
                  <QRCodeLayoutRoll
                    item={item}
                    tipoEtiqueta={tipoEtiqueta}
                    logotipo={logotipo}
                    buildQRCodeLink={buildQRCodeLink}
                    buildImageSrc={(it, t) => buildImageSrc(it, t, logotipo)}
                    line1Text={resolveText(item, tipoEtiqueta, desc1Key)}
                    line2Text={resolveText(item, tipoEtiqueta, desc2Key)}
                    fontPt={fontPt}
                  />
                </Single100x50>
              ))}

            {/* ===== 6×4 cm (1 por rolo) ===== */}
            {formatoEtiqueta === '6x4-single' &&
              data.map((item, i) => (
                <Single60x40 key={`s64-${i}`}>
                  <QRCodeLayoutRoll64
                    item={item}
                    tipoEtiqueta={tipoEtiqueta}
                    logotipo={logotipo}
                    buildQRCodeLink={buildQRCodeLink}
                    buildImageSrc={(it, t) => buildImageSrc(it, t, logotipo)}
                    line1Text={resolveText(item, tipoEtiqueta, desc1Key)}
                    line2Text={resolveText(item, tipoEtiqueta, desc2Key)}
                    fontPt={fontPt}
                  />
                </Single60x40>
              ))}
          </div>
        </WhiteSheet>
      </PDFContainer>

      <Button onClick={handlePrint}>Imprimir</Button>
    </StyledQRcodeList>
  );
};
